/* eslint-disable react/no-unstable-nested-components */
import { faPen, faPlus, faSearch, faTrash } from "@fortawesome/free-solid-svg-icons"
import HeaderFilters from "components/Shared/HeaderFilters/HeaderFilters"
import { useEffect, useState } from "react"
import { Modal, TextInput, Pagination, Search, Loading } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import BaseTable, { AutoResizer, Column, ColumnShape } from "react-base-table"
import { SubmitHandler, useForm } from "react-hook-form"
import {
  useAddPriceTypeMutation,
  useUpdatePriceTypeMutation,
  useDeletePriceTypeMutation,
  useSearchPriceTypeQuery,
} from "services/core/priceType"
import { PriceType } from "interfaces/dataInterfaces"
import { GET_ITEM_LIMIT, MARKET_MODULE } from "constants/constants"
import useCommon from "hooks/useCommon"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"

type Inputs = {
  name: string
  extendedName: string
}
const permissionsProps = {
  moduleId: MARKET_MODULE,
  subModule: "ccpucs",
}
export default function PricetypesPage() {
  const { showSnackBar } = useCommon()

  const [pageNumber, setPageNumber] = useState(1)
  const [getItemLimit, setGetItemLimit] = useState(GET_ITEM_LIMIT)
  const [query, setQuery] = useState<string | undefined>("")
  const [inputText, setInputText] = useState<string | undefined>(undefined)
  const [showNewPriceTypeModal, setShowNewPriceTypeModal] = useState(false)

  const { data: priceTypes, isFetching: isLoadingPriceTypes } = useSearchPriceTypeQuery({
    skip: pageNumber === 1 ? 0 : (pageNumber - 1) * getItemLimit,
    take: getItemLimit,
    query,
  })
  const [addPriceType, { isLoading: isLoadingAdd }] = useAddPriceTypeMutation()
  const [updatePriceType, { isLoading: isLoadingEdit }] = useUpdatePriceTypeMutation()
  const [deletePriceType, { isLoading: isLoadingDelete }] = useDeletePriceTypeMutation()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<Inputs>()

  const [actionItem, setActionItem] = useState<{
    item: PriceType
    action: "edit" | "delete"
  } | null>(null)

  const onSubmit: SubmitHandler<Inputs> = async data => {
    if (actionItem?.action === "edit") {
      try {
        await updatePriceType({
          priceTypeID: actionItem.item.priceTypeID,
          priceTypeName: data.name,
        }).unwrap()
        setActionItem(null)
        showSnackBar("Price type updated successfully!")
      } catch (error) {
        showSnackBar("Price type not updated", "error")
      }
    } else {
      try {
        await addPriceType({ priceTypeName: data.name }).unwrap()
        setShowNewPriceTypeModal(false)
        showSnackBar("Price type added successfully!")
      } catch (error) {
        showSnackBar("Price type not added", "error")
      }
    }
    reset()
  }

  const columns: Array<ColumnShape<PriceType>> = [
    {
      key: "priceTypeID",
      dataKey: "priceTypeID",
      title: "ID",
      width: 80,
      maxWidth: 80,
    },
    {
      key: "priceTypeName",
      dataKey: "priceTypeName",
      title: "Name",
      width: 200,
    },
    {
      key: "action",
      width: 0,
      align: Column.Alignment.CENTER,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => (
        <PermissionWrapper {...permissionsProps} feature="edit">
          <>
            <Button
              action={() =>
                setActionItem({
                  item: rowData,
                  action: "edit",
                })
              }
              label="Edit"
              className="btn-full blue action-btn"
              icon={faPen}
            />

            <Button
              action={() =>
                setActionItem({
                  item: rowData,
                  action: "delete",
                })
              }
              label="Delete"
              className="btn-full danger action-btn"
              style={{ marginLeft: "10px" }}
              icon={faTrash}
            />
          </>
        </PermissionWrapper>
      ),
    },
  ]

  const handleDelete = async (id: number) => {
    try {
      await deletePriceType(id)
      setActionItem(null)
      showSnackBar("Price type deleted successfully")
    } catch (error) {
      showSnackBar("Price type not deleted", "error")
    }
  }

  const handleSearch = (e: any) => {
    setInputText(e)
  }

  const handleSubmitQuery = () => {
    setPageNumber(1)
    setQuery(inputText)
  }

  const handleSearchKey = (e: any) => {
    if (inputText && e?.code === "Enter") handleSubmitQuery()
  }

  useEffect(() => {
    if (!inputText && query) setQuery("")
  }, [inputText])

  useEffect(() => {
    reset()
    if (actionItem?.action === "edit") {
      setValue("name", actionItem?.item.priceTypeName)
    }
  }, [actionItem])

  const handleNewEditModalClose = () => {
    reset()
    if (actionItem) {
      setActionItem(null)
    } else {
      setShowNewPriceTypeModal(false)
    }
  }

  return (
    <>
      {(isLoadingAdd || isLoadingEdit || isLoadingDelete) && <Loading />}
      <HeaderFilters>
        <div className="d-flex" style={{ width: "300px" }}>
          <Search
            id="query"
            name="query"
            placeholder="Type here to filter results..."
            className="text-input mr-2"
            value={query || ""}
            onChange={e => handleSearch(e)}
            onKeyUp={e => handleSearchKey(e)}
          />
          <Button action={handleSubmitQuery} label="Search" className="btn-full blue" icon={faSearch} />
        </div>

        <PermissionWrapper {...permissionsProps} feature="addNew">
          <Button
            action={() => setShowNewPriceTypeModal(true)}
            label="Add new price type"
            className="btn-full green"
            icon={faPlus}
          />
        </PermissionWrapper>
      </HeaderFilters>

      <div
        className={isLoadingPriceTypes ? "mt-2 d-flex align-center justify-center" : "mt-2"}
        style={{
          height: "100%",
          minHeight: "60vh",
        }}
      >
        {isLoadingPriceTypes ? (
          <Loading withOverlay={false} />
        ) : (
          <AutoResizer>
            {({ width, height }) => (
              <BaseTable
                data={priceTypes?.items}
                width={width}
                height={height}
                rowKey="priceTypeID"
                className="custom-table"
                rowHeight={38}
                headerHeight={45}
                columns={columns}
                emptyRenderer={() => <p>No records to display</p>}
              />
            )}
          </AutoResizer>
        )}
      </div>
      <Modal
        open={actionItem?.action === "delete"}
        type="danger"
        modalHeading="Confirm Delete"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestSubmit={() => handleDelete(actionItem!.item.priceTypeID)}
        onRequestClose={() => setActionItem(null)}
      >
        Are you sure you want to delete the selected record?
      </Modal>

      <Modal
        open={showNewPriceTypeModal || actionItem?.action === "edit"}
        modalHeading={actionItem?.action === "edit" ? "Modify price type" : "Add new price type"}
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleSubmit(onSubmit)}
        onRequestClose={handleNewEditModalClose}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextInput
            labelText="Name"
            placeholder="Price type name"
            defaultValue={actionItem?.item.priceTypeName}
            invalid={errors.name}
            invalidText={errors.name?.message}
            {...register("name", { required: "Name is required" })}
          />
        </form>
      </Modal>

      <Pagination
        totalItems={priceTypes?.count ?? 0}
        pageSize={getItemLimit}
        pageSizes={[10, 20, 30]}
        page={pageNumber}
        onChange={event => {
          setPageNumber((event as any).page)
          setGetItemLimit((event as any).pageSize)
        }}
      />
    </>
  )
}
